import { supabase } from "@/integrations/supabase/client";
import { Tables, TablesInsert } from "@/integrations/supabase/types";

export type EventGroupChat = Tables<"event_group_chats">;
export type GroupMessage = Tables<"group_messages">;
export type GroupMessageReaction = Tables<"group_message_reactions">;
export type GroupTypingStatus = Tables<"group_typing_status">;

export type GroupMessageInsert = TablesInsert<"group_messages">;

export interface GroupMessageWithUser extends GroupMessage {
  user: {
    username: string;
    display_name: string;
  };
}

export interface GroupChatWithDetails extends EventGroupChat {
  event: {
    title: string;
    created_by: string;
  };
  participant_count: number;
}

export const groupChatService = {
  async getEventGroupChat(eventId: string): Promise<EventGroupChat | null> {
    const { data, error } = await supabase
      .from("event_group_chats")
      .select("*")
      .eq("event_id", eventId)
      .single();

    if (error) {
      console.error("Error fetching event group chat:", error);
      return null;
    }

    return data;
  },

  async getGroupMessages(groupChatId: string, limit: number = 50): Promise<GroupMessageWithUser[]> {
    const { data, error } = await supabase
      .from("group_messages")
      .select(`
        *,
        user_profiles!fk_group_messages_user_profiles(
          display_name,
          first_name,
          last_name
        )
      `)
      .eq("group_chat_id", groupChatId)
      .eq("is_deleted", false)
      .order("created_at", { ascending: true })
      .limit(limit);

    if (error) {
      console.error("Error fetching group messages:", error);
      throw error;
    }

    return data?.map(message => ({
      ...message,
      user: {
        username: message.user_profiles?.display_name || 
                 `${message.user_profiles?.first_name || ''} ${message.user_profiles?.last_name || ''}`.trim() || 
                 'Unknown User',
        display_name: message.user_profiles?.display_name || 'Unknown User'
      }
    })) || [];
  },

  async sendGroupMessage(groupChatId: string, content: string): Promise<GroupMessage> {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    
    if (userError || !userData.user) {
      throw new Error("User not authenticated");
    }

    const { data, error } = await supabase
      .from("group_messages")
      .insert({
        group_chat_id: groupChatId,
        sender_id: userData.user.id,
        content: content.trim(),
        message_type: "text"
      })
      .select()
      .single();

    if (error) {
      console.error("Error sending group message:", error);
      throw error;
    }

    return data;
  },

  async deleteGroupMessage(messageId: string): Promise<void> {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    
    if (userError || !userData.user) {
      throw new Error("User not authenticated");
    }

    const { error } = await supabase
      .from("group_messages")
      .update({
        is_deleted: true,
        deleted_at: new Date().toISOString(),
        deleted_by: userData.user.id
      })
      .eq("id", messageId);

    if (error) {
      console.error("Error deleting group message:", error);
      throw error;
    }
  },

  async addGroupMessageReaction(messageId: string, emoji: string): Promise<GroupMessageReaction> {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    
    if (userError || !userData.user) {
      throw new Error("User not authenticated");
    }

    const { data, error } = await supabase
      .from("group_message_reactions")
      .insert({
        message_id: messageId,
        user_id: userData.user.id,
        emoji: emoji
      })
      .select()
      .single();

    if (error) {
      console.error("Error adding group message reaction:", error);
      throw error;
    }

    return data;
  },

  async removeGroupMessageReaction(messageId: string, emoji: string): Promise<void> {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    
    if (userError || !userData.user) {
      throw new Error("User not authenticated");
    }

    const { error } = await supabase
      .from("group_message_reactions")
      .delete()
      .eq("message_id", messageId)
      .eq("user_id", userData.user.id)
      .eq("emoji", emoji);

    if (error) {
      console.error("Error removing group message reaction:", error);
      throw error;
    }
  },

  async getGroupMessageReactions(groupChatId: string): Promise<{ [messageId: string]: GroupMessageReaction[] }> {
    const { data, error } = await supabase
      .from("group_message_reactions")
      .select(`
        *,
        group_messages!inner(group_chat_id)
      `)
      .eq("group_messages.group_chat_id", groupChatId);

    if (error) {
      console.error("Error fetching group message reactions:", error);
      return {};
    }

    const groupedReactions: { [messageId: string]: GroupMessageReaction[] } = {};
    data?.forEach((reaction: any) => {
      if (!groupedReactions[reaction.message_id]) {
        groupedReactions[reaction.message_id] = [];
      }
      groupedReactions[reaction.message_id].push(reaction);
    });

    return groupedReactions;
  },

  async updateGroupTypingStatus(groupChatId: string, isTyping: boolean): Promise<void> {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    
    if (userError || !userData.user) {
      throw new Error("User not authenticated");
    }

    const { error } = await supabase
      .from("group_typing_status")
      .upsert({
        group_chat_id: groupChatId,
        user_id: userData.user.id,
        is_typing: isTyping,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'group_chat_id,user_id',
        ignoreDuplicates: false
      });

    if (error) {
      console.error("Error updating group typing status:", error);
      throw error;
    }
  },

  async getEventParticipants(eventId: string) {
    const { data, error } = await supabase
      .from("event_attendees")
      .select(`
        *,
        user_profiles(
          display_name,
          first_name,
          last_name,
          photo_urls
        )
      `)
      .eq("event_id", eventId)
      .eq("status", "approved");

    if (error) {
      console.error("Error fetching event participants:", error);
      throw error;
    }

    return data?.map(attendee => ({
      ...attendee,
      user: {
        username: attendee.user_profiles?.display_name || 
                 `${attendee.user_profiles?.first_name || ''} ${attendee.user_profiles?.last_name || ''}`.trim() || 
                 'Unknown User',
        display_name: attendee.user_profiles?.display_name || 'Unknown User',
        photo_urls: attendee.user_profiles?.photo_urls || []
      }
    })) || [];
  }
};
